import React, { useMemo, useState } from 'react';
import { X, MessageCircle, Check, Heart, AlertCircle, Eye, EyeOff } from 'lucide-react';
import ActivityFilters from './ActivityFilters';

const ActivityModal = ({
    isOpen,
    onClose,
    teamResponses = [],
    comments = {},
    reactions = [],
    features = [],
    lastViewedTimestamp,
    onActivityClick
}) => {
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [timeFilter, setTimeFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    
    const featureMap = useMemo(() => {
        return features.reduce((acc, feature) => {
            if (feature?.reference) {
                acc[feature.reference] = feature;
            }
            return acc;
        }, {});
    }, [features]);

    const activities = useMemo(() => {
        const allActivities = [];
        const lastViewed = new Date(lastViewedTimestamp || new Date(0));

        // Add team responses
        teamResponses.forEach((response) => {
            if (!response?.feature_reference) return;
            
            const feature = featureMap[response.feature_reference];
            allActivities.push({
                type: 'response',
                user: response.user_name || 'Unknown User',
                timestamp: new Date(response.created_at),
                feature: feature?.name || response.feature_reference,
                feature_reference: response.feature_reference,
                value: response.response_value,
                icon: <Check className="w-5 h-5 text-green-600" />,
                isNew: new Date(response.created_at) > lastViewed
            });
        });

        // Add comments
        Object.values(comments).flat().forEach((comment) => {
            if (!comment?.feature_reference) return;
            
            const feature = featureMap[comment.feature_reference];
            allActivities.push({
                type: 'comment',
                user: comment.user_name || 'Unknown User',
                timestamp: new Date(comment.created_at),
                feature: feature?.name || comment.feature_reference,
                feature_reference: comment.feature_reference,
                content: comment.comment_text,
                icon: <MessageCircle className="w-5 h-5 text-blue-600" />,
                isNew: new Date(comment.created_at) > lastViewed,
                comment_id: comment.id
            });
        });

        // Add reactions
        reactions.forEach((reaction) => {
            if (!reaction?.comment_id) return;
            
            const comment = Object.values(comments || {})
                .flat()
                .find(c => c?.id === reaction.comment_id);
            const feature = comment ? featureMap[comment.feature_reference] : null;
            
            allActivities.push({
                type: 'reaction',
                user: reaction.user_name || 'Unknown User',
                timestamp: new Date(reaction.created_at),
                feature: feature?.name || 'Unknown Feature',
                feature_reference: comment?.feature_reference,
                emoji: reaction.emoji,
                commentText: comment?.comment_text || 'Comment not found',
                icon: <Heart className="w-5 h-5 text-red-600" />,
                isNew: new Date(reaction.created_at) > lastViewed,
                comment_id: comment?.id
            });
        });

        return allActivities.sort((a, b) => b.timestamp - a.timestamp);
    }, [teamResponses, comments, reactions, featureMap, lastViewedTimestamp]);

    const filteredActivities = activities.filter(activity => {
        // Unread filter
        if (showUnreadOnly && !activity.isNew) return false;

        // Type filter
        if (typeFilter !== 'all' && activity.type !== typeFilter) return false;

        // Time filter
        if (timeFilter !== 'all') {
            const activityTime = activity.timestamp;
            const now = new Date();
            const diff = now - activityTime;
            const dayInMs = 24 * 60 * 60 * 1000;

            switch (timeFilter) {
                case 'hour':
        if (diff > 60 * 60 * 1000) return false; // 1 hour in milliseconds
        break;
    case 'day':
        if (diff > dayInMs) return false;
        break;
                case '3days':
                    if (diff > 3 * dayInMs) return false;
                    break;
                case 'week':
                    if (diff > 7 * dayInMs) return false;
                    break;
                case 'month':
                    if (diff > 30 * dayInMs) return false;
                    break;
            }
        }

        return true;
    });

    const getActivityDescription = (activity) => {
        switch (activity.type) {
            case 'response':
                return (
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <span className="font-medium">{activity.user}</span>
                            <span>selected</span>
                            <span className="px-2 py-1 text-sm rounded-full bg-gray-100">
                                {activity.value?.replace('_', ' ') || 'unknown'}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600">
                            for feature: {activity.feature}
                        </div>
                    </div>
                );
            
            case 'comment':
                return (
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <span className="font-medium">{activity.user}</span>
                            <span>commented on</span>
                            <span className="font-medium">{activity.feature}</span>
                        </div>
                        <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-md">
                            {activity.content}
                        </div>
                    </div>
                );
            
            case 'reaction':
                return (
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <span className="font-medium">{activity.user}</span>
                            <span>reacted with</span>
                            <span className="text-xl">{activity.emoji}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                            to: "{activity.commentText}"
                        </div>
                        <div className="text-xs text-gray-500">
                            on feature: {activity.feature}
                        </div>
                    </div>
                );
            
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] m-4 flex flex-col">
                <div>
                    <div className="flex justify-between items-center p-4 border-b">
                        <div>
                            <h2 className="text-xl font-semibold">Activity Log</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Showing all team activity across features
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                                className="flex items-center gap-2 px-3 py-1 text-sm rounded-md border hover:bg-gray-50"
                            >
                                {showUnreadOnly ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                {showUnreadOnly ? 'Show All' : 'Show Unread Only'}
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                    <ActivityFilters 
                        onTimeFilterChange={setTimeFilter}
                        onTypeFilterChange={setTypeFilter}
                    />
                </div>

                <div className="flex-1 overflow-y-auto">
                    {filteredActivities.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-8 text-gray-500">
                            <AlertCircle className="w-12 h-12 mb-2" />
                            <p>No {showUnreadOnly ? 'unread ' : ''}activity recorded yet</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {filteredActivities.map((activity, index) => (
                                <div
                                    key={index}
                                    onClick={() => onActivityClick?.(activity)}
                                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer
                                        ${activity.isNew ? 'bg-green-50 hover:bg-green-100' : ''}`}
                                >
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 mt-1">
                                            {activity.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {getActivityDescription(activity)}
                                            <div className="mt-2 text-xs text-gray-400">
                                                {activity.timestamp.toLocaleString()}
                                                {activity.isNew && (
                                                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                                        New
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivityModal;